<div class="uvs-container">

  <!-- ── Toolbar ──────────────────────────────────────────────────────────── -->
  <div class="uvs-toolbar">
    <span class="uvs-title">User Virtual Machines</span>
    <span class="uvs-count mat-body-2" *ngIf="dataSource.data.length">
      {{ dataSource.data.length }} VM(s)
    </span>

    <!-- Bulk actions -->
    <button mat-stroked-button
            [matMenuTriggerFor]="bulkMenu"
            [disabled]="!dataSource.data.length || isBulkBusy || isLoading">
      <mat-icon>flash_on</mat-icon>
      Bulk Actions
      <mat-icon>arrow_drop_down</mat-icon>
    </button>
    <mat-menu #bulkMenu="matMenu">
      <button mat-menu-item (click)="startAll()">
        <mat-icon>play_circle</mat-icon> Start All
      </button>
      <button mat-menu-item (click)="shutdownAll()">
        <mat-icon>power_settings_new</mat-icon> Shutdown All
      </button>
      <button mat-menu-item (click)="deallocateAll()">
        <mat-icon>stop_circle</mat-icon> Deallocate All
      </button>
      <mat-divider *ngIf="canDestroyResources"></mat-divider>
      <button mat-menu-item *ngIf="canDestroyResources" (click)="destroyAllVMs()" class="uvs-danger-item">
        <mat-icon color="warn">delete_forever</mat-icon>
        <span class="uvs-danger-text">Destroy All VMs</span>
      </button>
    </mat-menu>

    <button mat-stroked-button (click)="refreshRequested.emit()" [disabled]="isLoading || isBulkBusy">
      <mat-icon>refresh</mat-icon> Refresh
    </button>

    <button mat-stroked-button (click)="exportCsv()" [disabled]="!dataSource.data.length">
      <mat-icon>download</mat-icon> Export CSV
    </button>
  </div>

  <!-- ── Bulk loading bar ──────────────────────────────────────────────────── -->
  <mat-progress-bar *ngIf="isBulkBusy" mode="indeterminate" class="uvs-bulk-bar"></mat-progress-bar>

  <!-- ── Loading state ─────────────────────────────────────────────────────── -->
  <div *ngIf="isLoading" class="uvs-state-row">
    <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
    <span>Loading user VMs…</span>
  </div>

  <!-- ── Error state ───────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && loadError" class="uvs-error-row">
    <mat-icon>error_outline</mat-icon>
    <span>{{ loadError }}</span>
    <button mat-stroked-button (click)="refreshRequested.emit()">Retry</button>
  </div>

  <!-- ── Empty state ───────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && !loadError && !dataSource.data.length" class="uvs-empty">
    <mat-icon class="uvs-empty-icon">computer</mat-icon>
    <p>No participant VMs have been provisioned yet.</p>
  </div>

  <!-- ── Table ─────────────────────────────────────────────────────────────── -->
  <div class="uvs-table-wrap" *ngIf="!isLoading && !loadError && dataSource.data.length">
    <table mat-table [dataSource]="dataSource" class="uvs-table">

      <!-- Participant -->
      <ng-container matColumnDef="participant">
        <th mat-header-cell *matHeaderCellDef>Participant</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="row.participant">{{ row.participant ?? '—' }}</td>
      </ng-container>

      <!-- Login Id (email used to redeem / login) -->
      <ng-container matColumnDef="loginId">
        <th mat-header-cell *matHeaderCellDef>Login Id</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="row.redeemEmail">{{ truncate(row.redeemEmail, 24) }}</td>
      </ng-container>

      <!-- Subscription (name with tooltip showing ID + location) -->
      <ng-container matColumnDef="subscription">
        <th mat-header-cell *matHeaderCellDef>Subscription</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="getSubscriptionTooltip(row)">
          {{ truncate(getSubscriptionName(row), 22) }}</td>
      </ng-container>

      <!-- Resource Group -->
      <ng-container matColumnDef="resourceGroup">
        <th mat-header-cell *matHeaderCellDef>Resource Group</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="row.resourceGroupName">{{ truncate(row.resourceGroupName) }}</td>
      </ng-container>

      <!-- VM Details (popover with IDs) -->
      <ng-container matColumnDef="vmDetails">
        <th mat-header-cell *matHeaderCellDef>VM Details</th>
        <td mat-cell *matCellDef="let row" class="uvs-details-cell"
            (mouseenter)="showVmPopover(row, $event)" (mouseleave)="hideVmPopover()">
          <span class="uvs-detail-text">{{ truncate(row.vmName, 24) }}</span>
          <div class="uvs-info-panel" *ngIf="vmPopoverRow === row && row.vmName"
               [style.top.px]="vmPopoverPos.top" [style.left.px]="vmPopoverPos.left">
            <div class="uvs-info-item">
              <mat-icon>computer</mat-icon>
              <span class="uvs-info-val">{{ row.vmName }}</span>
            </div>
            <div class="uvs-info-item">
              <mat-icon>storage</mat-icon>
              <span class="uvs-info-val">{{ row.osDiskName || armName(row.osDiskId) }}</span>
            </div>
            <div class="uvs-info-item">
              <mat-icon>settings_ethernet</mat-icon>
              <span class="uvs-info-val">{{ row.networkInterfaceName || armName(row.networkInterfaceId) }}</span>
            </div>
            <div class="uvs-info-item" *ngIf="row.publicIpName || row.publicIpId">
              <mat-icon>public</mat-icon>
              <span class="uvs-info-val">{{ row.publicIpName || armName(row.publicIpId) }}</span>
            </div>
          </div>
        </td>
      </ng-container>

      <!-- Connection Details (IP + credentials popover) -->
      <ng-container matColumnDef="connectionDetails">
        <th mat-header-cell *matHeaderCellDef>Connection Details</th>
        <td mat-cell *matCellDef="let row" class="uvs-details-cell"
            (mouseenter)="showConnPopover(row, $event)" (mouseleave)="hideConnPopover()">
          <span class="uvs-detail-text">{{ row.publicIpAddress || '—' }}</span>
          <div class="uvs-info-panel uvs-conn-panel" *ngIf="connPopoverRow === row && row.adminUsername"
               [style.top.px]="connPopoverPos.top" [style.left.px]="connPopoverPos.left">
            <div class="uvs-info-item">
              <mat-icon>person</mat-icon>
              <span class="uvs-info-val">{{ row.adminUsername }}</span>
            </div>
            <div class="uvs-info-item">
              <mat-icon>lock</mat-icon>
              <span class="uvs-info-val">{{ row.adminPassword }}</span>
            </div>
          </div>
        </td>
      </ng-container>

      <!-- Power State -->
      <ng-container matColumnDef="powerState">
        <th mat-header-cell *matHeaderCellDef>State</th>
        <td mat-cell *matCellDef="let row">
          <span class="ps-badge" [ngClass]="powerStateClass(row)" *ngIf="powerStateLabel(row) !== '—'">
            {{ powerStateLabel(row) }}
          </span>
          <span *ngIf="powerStateLabel(row) === '—'" class="ps-badge ps-unknown">—</span>
        </td>
      </ng-container>

      <!-- Actions -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="uvs-actions-header">Actions</th>
        <td mat-cell *matCellDef="let row" class="uvs-actions-cell">
          <button mat-icon-button
                  [matMenuTriggerFor]="rowMenu"
                  [disabled]="isBusy(row) || isBulkBusy"
                  matTooltip="Actions">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #rowMenu="matMenu">
            <!-- Power actions (always visible) -->
            <button mat-menu-item (click)="startVM(row)">
              <mat-icon>play_circle</mat-icon> Start
            </button>
            <button mat-menu-item (click)="shutdownVM(row)">
              <mat-icon>power_settings_new</mat-icon> Shutdown
            </button>
            <button mat-menu-item (click)="deallocateVM(row)">
              <mat-icon>stop_circle</mat-icon> Deallocate
            </button>
            <!-- Destroy actions (gated) -->
            <mat-divider *ngIf="canDestroyResources"></mat-divider>
            <button mat-menu-item *ngIf="canDestroyResources" (click)="destroyVM(row)"
                    class="uvs-danger-item">
              <mat-icon color="warn">delete_forever</mat-icon>
              <span class="uvs-danger-text">Destroy VM</span>
            </button>
          </mat-menu>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"
          [class.uvs-row-busy]="isBusy(row)"></tr>
    </table>

    <mat-paginator
      [pageSizeOptions]="pageSizeOptions"
      [pageSize]="pageSize"
      appPersistPageSize="class:userVms:list"
      showFirstLastButtons>
    </mat-paginator>
  </div>

</div>

